<script setup>
import { reactive, computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import Draggable from 'vuedraggable';
import { useMapGetter } from 'dashboard/composables/store';
import CustomToolsAPI from 'dashboard/api/captain/customTools';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ParamRow from './ParamRow.vue';
import AuthConfig from './AuthConfig.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  tool: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('captainCustomTools/getUIFlags'),
};

const DEFAULT_PARAM = {
  name: '',
  type: 'string',
  description: '',
  required: false,
};

const initialState = {
  title: '',
  description: '',
  endpoint_url: '',
  http_method: 'GET',
  auth_type: 'none',
  auth_config: {},
  param_schema: [],
  trigger_conditions: [],
};

const SOCIAL_FIELD_KEYS = [
  'social_facebook',
  'social_github',
  'social_instagram',
  'social_telegram',
  'social_tiktok',
  'social_linkedin',
  'social_twitter',
];

const ENTITY_FIELD_GROUPS = [
  {
    key: 'contact',
    section: 'CONTACT_ATTRIBUTES_ALLOWLIST',
    fields: [
      'name',
      'email',
      'phone_number',
      'description',
      'city',
      'country',
      'country_code',
      ...SOCIAL_FIELD_KEYS,
    ],
  },
  {
    key: 'company',
    section: 'COMPANY_ATTRIBUTES_ALLOWLIST',
    fields: [
      'name',
      'domain',
      'email',
      'phone_number',
      'cnpj',
      'city',
      'country_code',
      'description',
      ...SOCIAL_FIELD_KEYS,
    ],
  },
  {
    key: 'deal',
    section: 'DEAL_ATTRIBUTES_ALLOWLIST',
    fields: [
      'name',
      'pipeline',
      'value',
      'currency',
      'products_of_interest',
      'last_purchase_at',
      'pipeline_stage',
    ],
  },
];

const state = reactive({ ...initialState });

const activeEntityTab = ref(null);

const toggleEntityTab = key => {
  activeEntityTab.value = activeEntityTab.value === key ? null : key;
};

const entityParamName = (entity, field) => `${entity.key}_${field}`;

const isEntityFieldSelected = (entity, field) =>
  state.param_schema.some(
    param => param.name === entityParamName(entity, field)
  );

const toggleEntityField = (entity, field) => {
  const name = entityParamName(entity, field);
  if (isEntityFieldSelected(entity, field)) {
    state.param_schema = state.param_schema.filter(
      param => param.name !== name
    );
    return;
  }
  const fieldLabel = t(
    `CAPTAIN.ASSISTANTS.FORM.${entity.section}.FIXED_FIELDS.${field}`
  );
  const entityLabel = t(
    `CAPTAIN.CUSTOM_TOOLS.FORM.ENTITY_FIELDS.${entity.key}`
  );
  state.param_schema.push({
    ...DEFAULT_PARAM,
    name,
    description: t(
      'CAPTAIN.CUSTOM_TOOLS.FORM.ENTITY_FIELDS.PARAM_DESCRIPTION',
      {
        field: fieldLabel.toLowerCase(),
        entity: entityLabel,
      }
    ),
  });
};

// Stable per-item id so editing the text doesn't recreate the input node.
let nextConditionId = 0;
const generateConditionId = () => {
  nextConditionId += 1;
  return nextConditionId;
};

const addCondition = () => {
  state.trigger_conditions.push({
    id: generateConditionId(),
    text: '',
    enabled: true,
  });
};

const removeCondition = index => {
  state.trigger_conditions.splice(index, 1);
};

// Populate form when in edit mode
watch(
  () => props.tool,
  newTool => {
    if (props.mode === 'edit' && newTool && newTool.id) {
      state.title = newTool.title || '';
      state.description = newTool.description || '';
      state.endpoint_url = newTool.endpoint_url || '';
      state.http_method = newTool.http_method || 'GET';
      state.auth_type = newTool.auth_type || 'none';
      state.auth_config = newTool.auth_config || {};
      state.param_schema = newTool.param_schema || [];
      state.trigger_conditions = (newTool.trigger_conditions || []).map(
        condition => ({
          id: generateConditionId(),
          text: condition.text || '',
          enabled: condition.enabled !== false,
        })
      );
    }
  },
  { immediate: true }
);

// OpenAI enforces a 64-char limit on function names. The backend slug is
// "custom_" (7 chars) + parameterized title, so cap the title conservatively.
const MAX_TOOL_NAME_LENGTH = 55;

const validationRules = {
  title: { required, maxLength: maxLength(MAX_TOOL_NAME_LENGTH) },
  endpoint_url: { required },
  http_method: { required },
  auth_type: { required },
};

const httpMethodOptions = computed(() => [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
]);

const authTypeOptions = computed(() => [
  { value: 'none', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE') },
  { value: 'bearer', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.BEARER') },
  { value: 'basic', label: t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.BASIC') },
  {
    value: 'api_key',
    label: t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.API_KEY'),
  },
]);

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(() =>
  props.mode === 'edit'
    ? formState.uiFlags.value.updatingItem
    : formState.uiFlags.value.creatingItem
);

const getErrorMessage = (field, errorKey) => {
  if (!v$.value[field].$error) return '';

  const failedRule = v$.value[field].$errors[0]?.$validator;
  if (failedRule === 'maxLength') {
    return t(`CAPTAIN.CUSTOM_TOOLS.FORM.${errorKey}.MAX_LENGTH_ERROR`, {
      max: MAX_TOOL_NAME_LENGTH,
    });
  }
  return t(`CAPTAIN.CUSTOM_TOOLS.FORM.${errorKey}.ERROR`);
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  endpoint_url: getErrorMessage('endpoint_url', 'ENDPOINT_URL'),
}));

const paramsRef = useTemplateRef('paramsRef');

const isParamsValid = () => {
  if (!paramsRef.value || paramsRef.value.length === 0) {
    return true;
  }
  return paramsRef.value.every(param => param.validate());
};

const removeParam = index => {
  state.param_schema.splice(index, 1);
};

const addParam = () => {
  state.param_schema.push({ ...DEFAULT_PARAM });
};

const handleCancel = () => emit('cancel');

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !isParamsValid()) {
    return;
  }

  emit('submit', {
    ...state,
    trigger_conditions: state.trigger_conditions.map(({ text, enabled }) => ({
      text,
      enabled,
    })),
  });
};

const isTesting = ref(false);
const testResult = ref(null);
const isTestDisabled = computed(() => state.endpoint_url.includes('{{'));

const handleTest = async () => {
  if (!state.endpoint_url) return;

  isTesting.value = true;
  testResult.value = null;
  try {
    const { data } = await CustomToolsAPI.test(state);
    const isOk = data.status >= 200 && data.status < 300;
    testResult.value = { success: isOk, status: data.status };
  } catch (e) {
    const message =
      e.response?.data?.error || t('CAPTAIN.CUSTOM_TOOLS.TEST.ERROR');
    testResult.value = { success: false, message };
  } finally {
    isTesting.value = false;
  }
};
</script>

<template>
  <form
    class="flex flex-col px-4 -mx-4 gap-4 max-h-[calc(100vh-200px)] overflow-y-scroll"
    @submit.prevent="handleSubmit"
  >
    <Input
      v-model="state.title"
      :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.TITLE.LABEL')"
      :placeholder="t('CAPTAIN.CUSTOM_TOOLS.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.description"
      :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.DESCRIPTION.LABEL')"
      :placeholder="t('CAPTAIN.CUSTOM_TOOLS.FORM.DESCRIPTION.PLACEHOLDER')"
      :rows="2"
    />

    <div class="flex gap-2">
      <div class="flex flex-col gap-1 w-28">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.HTTP_METHOD.LABEL') }}
        </label>
        <ComboBox
          v-model="state.http_method"
          :options="httpMethodOptions"
          class="[&>div>button]:bg-n-alpha-black2 [&_li]:font-mono [&_button]:font-mono [&>div>button]:outline-offset-[-1px]"
        />
      </div>
      <Input
        v-model="state.endpoint_url"
        :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.ENDPOINT_URL.LABEL')"
        :placeholder="t('CAPTAIN.CUSTOM_TOOLS.FORM.ENDPOINT_URL.PLACEHOLDER')"
        :message="formErrors.endpoint_url"
        :message-type="formErrors.endpoint_url ? 'error' : 'info'"
        class="flex-1"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPE.LABEL') }}
      </label>
      <ComboBox
        v-model="state.auth_type"
        :options="authTypeOptions"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <AuthConfig
      v-model:auth-config="state.auth_config"
      :auth-type="state.auth_type"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAMETERS.LABEL') }}
      </label>
      <p class="text-xs text-n-slate-11 -mt-1">
        {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.PARAMETERS.HELP_TEXT') }}
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.ENTITY_FIELDS.HELP_TEXT') }}
      </p>
      <div class="flex gap-2">
        <button
          v-for="entity in ENTITY_FIELD_GROUPS"
          :key="entity.key"
          type="button"
          class="px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors duration-200"
          :class="
            activeEntityTab === entity.key
              ? 'border-n-brand bg-n-alpha-1 text-n-slate-12'
              : 'border-n-weak bg-n-alpha-black2 text-n-slate-11 hover:border-n-slate-6'
          "
          @click="toggleEntityTab(entity.key)"
        >
          {{ t(`CAPTAIN.CUSTOM_TOOLS.FORM.ENTITY_FIELDS.${entity.key}`) }}
        </button>
      </div>
      <template v-for="entity in ENTITY_FIELD_GROUPS" :key="entity.key">
        <div
          v-if="activeEntityTab === entity.key"
          class="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 border border-n-weak rounded-lg"
        >
          <label
            v-for="field in entity.fields"
            :key="field"
            class="flex items-center gap-2 text-sm text-n-slate-12"
          >
            <input
              type="checkbox"
              :checked="isEntityFieldSelected(entity, field)"
              @change="toggleEntityField(entity, field)"
            />
            {{
              t(
                `CAPTAIN.ASSISTANTS.FORM.${entity.section}.FIXED_FIELDS.${field}`
              )
            }}
          </label>
        </div>
      </template>
      <ul v-if="state.param_schema.length > 0" class="grid gap-2 list-none">
        <ParamRow
          v-for="(param, index) in state.param_schema"
          :key="index"
          ref="paramsRef"
          v-model:name="param.name"
          v-model:type="param.type"
          v-model:description="param.description"
          v-model:required="param.required"
          @remove="removeParam(index)"
        />
      </ul>
      <Button
        type="button"
        sm
        ghost
        blue
        icon="i-lucide-plus"
        :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.ADD_PARAMETER')"
        @click="addParam"
      />
    </div>

    <div class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.CUSTOM_TOOLS.FORM.TRIGGER_CONDITIONS.LABEL') }}
      </label>
      <Draggable
        v-model="state.trigger_conditions"
        tag="div"
        item-key="id"
        class="flex flex-col gap-2"
        handle=".drag-handle"
      >
        <template #item="{ element: condition, index }">
          <div class="flex items-center gap-2">
            <Icon
              icon="i-lucide-grip-vertical"
              class="drag-handle size-4 text-n-slate-11 cursor-move flex-shrink-0"
            />
            <Switch v-model="condition.enabled" />
            <Input v-model="condition.text" class="flex-1" />
            <Button
              type="button"
              icon="i-lucide-x"
              variant="ghost"
              color="slate"
              size="sm"
              @click="removeCondition(index)"
            />
          </div>
        </template>
      </Draggable>
      <Button
        type="button"
        icon="i-lucide-plus"
        variant="ghost"
        size="sm"
        class="!w-fit !text-n-brand hover:!text-n-brand"
        :label="t('CAPTAIN.CUSTOM_TOOLS.FORM.TRIGGER_CONDITIONS.ADD')"
        @click="addCondition"
      />
    </div>

    <div class="flex flex-col gap-2">
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-lucide-play"
        :label="t('CAPTAIN.CUSTOM_TOOLS.TEST.BUTTON')"
        :is-loading="isTesting"
        :disabled="isTesting || !state.endpoint_url || isTestDisabled"
        @click="handleTest"
      />
      <p v-if="isTestDisabled" class="text-xs text-n-slate-11">
        {{ t('CAPTAIN.CUSTOM_TOOLS.TEST.DISABLED_HINT') }}
      </p>
      <div
        v-if="testResult"
        class="flex items-center gap-2 px-3 py-2 text-xs rounded-lg"
        :class="
          testResult.success
            ? 'bg-n-teal-2 text-n-teal-11'
            : 'bg-n-ruby-2 text-n-ruby-11'
        "
      >
        <span
          :class="
            testResult.success ? 'i-lucide-check-circle' : 'i-lucide-x-circle'
          "
          class="size-3.5 shrink-0"
        />
        {{
          testResult.status
            ? t('CAPTAIN.CUSTOM_TOOLS.TEST.SUCCESS', {
                status: testResult.status,
              })
            : testResult.message
        }}
      </div>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('CAPTAIN.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="
          t(mode === 'edit' ? 'CAPTAIN.FORM.EDIT' : 'CAPTAIN.FORM.CREATE')
        "
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
